/**
 * Central date/time formatting utility (issue #424).
 *
 * Formats dates using the authenticated user's preferred `locale` + `timezone`
 * (from the Redux `preferences` slice), so every call site renders consistently
 * instead of hard-coding `'en-US'` and the browser's local zone. Uses `date-fns`
 * for robust ISO parsing/validity and `Intl.DateTimeFormat` for locale + IANA
 * timezone-aware rendering.
 *
 * Callers pass only the value; locale/timezone are resolved from the store. An
 * explicit override may be supplied for tests or special cases.
 */

import { parseISO, isValid } from 'date-fns';
import { store } from '../store';

export type DateInput = string | number | Date;

export interface DateFormatOptions extends Intl.DateTimeFormatOptions {
  /** Override the user's preferred BCP-47 locale. */
  locale?: string;
  /** Override the user's preferred IANA timezone. */
  timezone?: string;
}

/** Fallbacks used before preferences load or when the slice is unavailable. */
const FALLBACK_LOCALE = 'en-US';
const FALLBACK_TIMEZONE =
  typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';

/**
 * Resolve the active locale + timezone from the preferences slice, falling back
 * to sensible defaults when the store has no preferences state yet.
 */
export function getActiveDatePrefs(): { locale: string; timezone: string } {
  // Defensive: in some test contexts the store module is mocked without a
  // `store` export. Fall back to browser/platform defaults rather than throw.
  const state = typeof store?.getState === 'function' ? store.getState() : undefined;
  const prefs = (state as { preferences?: { locale?: string; timezone?: string } } | undefined)
    ?.preferences;
  return {
    locale: prefs?.locale || FALLBACK_LOCALE,
    timezone: prefs?.timezone || FALLBACK_TIMEZONE,
  };
}

/**
 * Coerce supported inputs into a Date. Strings are parsed as ISO-8601 via
 * `date-fns`; numbers are treated as epoch milliseconds. Returns `null` for
 * unparseable/invalid input so callers can render a placeholder.
 */
export function toDate(value: DateInput): Date | null {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isValid(d) ? d : null;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

function resolve(options?: DateFormatOptions): {
  locale: string;
  timezone: string;
  intlOptions: Intl.DateTimeFormatOptions;
} {
  const active = getActiveDatePrefs();
  const { locale, timezone, ...intlOptions } = options ?? {};
  return {
    locale: locale || active.locale,
    timezone: timezone || active.timezone,
    intlOptions,
  };
}

/**
 * Format a date+time honoring the user's locale + timezone. Placeholder
 * returned for invalid input.
 */
export function formatDateTime(
  value: DateInput | null | undefined,
  options?: DateFormatOptions,
  placeholder = '—'
): string {
  if (value === null || value === undefined) return placeholder;
  const date = toDate(value);
  if (!date) return placeholder;
  const { locale, timezone, intlOptions } = resolve(options);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
    ...intlOptions,
  }).format(date);
}

/** Format the date portion only (no time), honoring locale + timezone. */
export function formatDate(
  value: DateInput | null | undefined,
  options?: DateFormatOptions,
  placeholder = '—'
): string {
  return formatDateTime(value, { dateStyle: 'medium', ...options }, placeholder);
}

/** Format the time portion only (no date), honoring locale + timezone. */
export function formatTime(
  value: DateInput | null | undefined,
  options?: DateFormatOptions,
  placeholder = '—'
): string {
  if (value === null || value === undefined) return placeholder;
  const date = toDate(value);
  if (!date) return placeholder;
  const { locale, timezone, intlOptions } = resolve(options);
  return new Intl.DateTimeFormat(locale, {
    timeStyle: 'short',
    timeZone: timezone,
    ...intlOptions,
  }).format(date);
}
